<template>
    <jet-dialog-modal :show="show" @close="cancel" max-width="md">
        <template #title>
            {{ title }}
        </template>

        <template #content>
            <div class="space-y-4">

                <div class="grid grid-cols-2 gap-4">
                    <!-- Amount -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            {{ $t('offers.priceCalc.amount') }}
                            <span v-if="!readonly" class="text-red-500">*</span>
                        </label>
                        <template v-if="readonly">
                            <p class="text-sm text-gray-900 font-medium">{{ formatNumber(entry.amount) }}</p>
                        </template>
                        <template v-else>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                v-model.number="entry.amount"
                                @input="recalculate"
                                class="block w-full border-gray-300 focus:ring-minplan-blue focus:border-minplan-blue sm:text-sm"
                                :class="{ 'border-red-400': errors.amount }"
                                placeholder="0.00"
                            />
                            <p v-if="errors.amount" class="text-red-500 text-xs mt-1">{{ errors.amount }}</p>
                        </template>
                    </div>

                    <!-- Unit -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            {{ $t('offers.priceCalc.unit') }}
                            <span v-if="!readonly" class="text-red-500">*</span>
                        </label>
                        <template v-if="readonly">
                            <p class="text-sm text-gray-900 font-medium">{{ entry.unit || '—' }}</p>
                        </template>
                        <template v-else>
                            <select
                                v-model="entry.unit"
                                class="block w-full border-gray-300 focus:ring-minplan-blue focus:border-minplan-blue sm:text-sm"
                                :class="{ 'border-red-400': errors.unit }"
                            >
                                <option value="" disabled>— {{ $t('offers.priceCalc.selectUnit') }} —</option>
                                <option v-for="unit in units" :key="unit" :value="unit">{{ unit }}</option>
                            </select>
                            <p v-if="errors.unit" class="text-red-500 text-xs mt-1">{{ errors.unit }}</p>
                        </template>
                    </div>
                </div>

                <!-- Price per Unit -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        {{ $t('offers.priceCalc.pricePerUnit') }}
                        <span v-if="entry.unit" class="text-xs font-normal text-gray-500 italic"> (CHF / {{ entry.unit }})</span>
                        <span v-if="!readonly" class="text-red-500">*</span>
                    </label>
                    <template v-if="readonly">
                        <p class="text-sm text-gray-900 font-medium">{{ formatNumber(entry.price_per_unit) }}</p>
                    </template>
                    <template v-else>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            v-model.number="entry.price_per_unit"
                            @input="recalculate"
                            class="block w-full border-gray-300 focus:ring-minplan-blue focus:border-minplan-blue sm:text-sm"
                            :class="{ 'border-red-400': errors.price_per_unit }"
                            placeholder="0.00"
                        />
                        <p v-if="errors.price_per_unit" class="text-red-500 text-xs mt-1">{{ errors.price_per_unit }}</p>
                    </template>
                </div>

                <!-- Total (computed, read-only) -->
                <div class="pt-2 border-t border-gray-200">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        {{ $t('offers.priceCalc.total') }}
                    </label>
                    <p class="text-base font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                        {{ formatNumber(entry.total) }}
                    </p>
                </div>

            </div>
        </template>

        <template #footer>
            <div class="flex justify-end space-x-3">
                <button
                    type="button"
                    @click="cancel"
                    class="inline-block uppercase font-semibold text-xs tracking-widest"
                >
                    {{ $t("actions.close") }}
                </button>
                <primary-button
                    v-if="!readonly"
                    type="button"
                    @click="save"
                >
                    {{ $t("actions.save") }}
                </primary-button>
            </div>
        </template>
    </jet-dialog-modal>
</template>

<script>
import JetDialogModal from "@/Jetstream/DialogModal";
import PrimaryButton from "@/Jetstream/PrimaryButton";

export default {
    name: "PriceCalculationModal",
    components: { JetDialogModal, PrimaryButton },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: "Price Calculation",
        },
        units: {
            type: Array,
            default: () => ["Stk.", "m²", "m", "Std."],
        },
        // Accepts the stored JSON array; uses first element (or empty)
        initialRows: {
            type: Array,
            default: () => [],
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["cancel", "save"],
    data() {
        return {
            entry: { unit: "", price_per_unit: null, amount: null, total: 0 },
            errors: {},
        };
    },
    watch: {
        show(val) {
            if (val) {
                this.initEntry();
                this.errors = {};
            }
        },
        initialRows: {
            handler() {
                this.initEntry();
            },
            deep: true,
        },
    },
    methods: {
        initEntry() {
            const first = this.initialRows && this.initialRows.length > 0 ? this.initialRows[0] : null;
            this.entry = {
                // Bis 22.09.2026 hiess die Einheit «STK». Bestehende Offerten
                // tragen das noch; sie erscheinen als «Stk.» und werden beim
                // nächsten Speichern so abgelegt.
                unit: first?.unit === "STK" ? "Stk." : first?.unit || "",
                price_per_unit: this.sanitizeNumber(first?.price_per_unit),
                amount: this.sanitizeNumber(first?.amount),
                total: this.sanitizeNumber(first?.total) || 0,
            };
        },
        sanitizeNumber(val) {
            if (val === null || val === undefined || val === "") return null;
            if (typeof val === "number") return val;
            // Remove thousands separators (comma, space, or Swiss apostrophe)
            // Assuming . is the decimal separator as per the user's example "72,576.00"
            const cleaned = val.toString().replace(/[, ']/g, "");
            return parseFloat(cleaned);
        },
        recalculate() {
            const pp = this.sanitizeNumber(this.entry.price_per_unit) || 0;
            const am = this.sanitizeNumber(this.entry.amount) || 0;
            this.entry.total = Math.round(pp * am * 100) / 100;
        },
        validate() {
            this.errors = {};
            let valid = true;
            if (!this.entry.unit) {
                this.errors.unit = this.$t("offers.priceCalc.errorUnit");
                valid = false;
            }
            if (this.entry.price_per_unit === null || this.entry.price_per_unit === "" || isNaN(this.entry.price_per_unit) || parseFloat(this.entry.price_per_unit) < 0) {
                this.errors.price_per_unit = this.$t("offers.priceCalc.errorPricePerUnit");
                valid = false;
            }
            if (this.entry.amount === null || this.entry.amount === "" || isNaN(this.entry.amount) || parseFloat(this.entry.amount) < 0) {
                this.errors.amount = this.$t("offers.priceCalc.errorAmount");
                valid = false;
            }
            return valid;
        },
        save() {
            if (!this.validate()) return;
            // Emit as array with single item for backend compatibility
            this.$emit("save", [{ ...this.entry }]);
        },
        cancel() {
            this.$emit("cancel");
        },
        formatNumber(val) {
            if (val === null || val === undefined || val === "") return "0.00";
            return parseFloat(val).toLocaleString("de-CH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
    },
    created() {
        this.initEntry();
    },
};
</script>

<style scoped></style>
